import React, { forwardRef, ReactNode } from 'react';
import clsx from 'clsx';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps {
  children?: ReactNode;
  className?: string;
  onClick?: () => void;
  hover?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  variant?: 'default' | 'outlined' | 'elevated';
  size?: 'sm' | 'md' | 'lg';
  hoverable?: boolean;
  padding?: CardPadding;
  backgroundColor?: string;
  borderRadius?: string;
  shadow?: string;
  loading?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
}

const paddingClass: Record<CardPadding, string> = {
  none: 'p-0',
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-6',
};

const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    children,
    className = '',
    onClick,
    hover = false,
    header,
    footer,
    variant = 'default',
    size = 'md',
    hoverable,
    padding = 'md',
    backgroundColor = 'bg-eg-blanc',
    borderRadius = 'rounded-md',
    shadow = '',
    loading = false,
    disabled = false,
    ...rest
  },
  ref
) {
  // Charte OmnIA-EG : surface crème, filet net, pas d'ombre portée. La
  // séparation se fait par la bordure et par le fond, pas par le relief.
  const baseClasses = 'border border-eg-bord';
  const hoverClasses =
    hover || hoverable ? 'hover:border-[#C9D0C9] hover:bg-[#EEF2ED] transition-colors cursor-pointer' : '';

  const variantClasses = {
    default: 'bg-eg-blanc border-eg-bord',
    outlined: 'bg-eg-blanc border-[#DDE2DD]',
    elevated: 'bg-eg-blanc border-[#DDE2DD]'
  };
  
  const sizeClasses = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  };
  
  const classes = clsx(
    baseClasses,
    variantClasses[variant],
    // Ne pas appliquer sizeClasses si padding est 'none'
    padding !== 'none' ? sizeClasses[size] : '',
    hoverClasses,
    backgroundColor,
    borderRadius,
    shadow,
    // Ne pas appliquer paddingClass si padding est 'none'
    padding !== 'none' ? paddingClass[padding] : '',
    'transition-colors',
    disabled && 'opacity-60 pointer-events-none',
    className
  );
  

  
  const commonProps = {
    ref,
    className: classes,
    'aria-busy': loading || undefined,
    'aria-disabled': disabled || undefined,
    style: rest.style,
  };

  if (onClick) {
    return (
      <div {...commonProps} onClick={onClick} role="button" tabIndex={0}>
        {header && (
          <div className="mb-4 border-b border-eg-bordure pb-3">
            {typeof header === 'string' ? (
              <h3 className="text-[13px] font-semibold text-eg-encre">{header}</h3>
            ) : (
              header
            )}
          </div>
        )}
        <div>{children}</div>
        {footer && (
          <div className="mt-4 border-t border-eg-bordure pt-3">
            {typeof footer === 'string' ? (
              <p className="text-[13px] text-eg-gris">{footer}</p>
            ) : (
              footer
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div {...commonProps}>
      {header && (
        <div className="mb-4 border-b border-eg-bordure pb-3">
          {typeof header === 'string' ? (
            <h3 className="text-[13px] font-semibold text-eg-encre">{header}</h3>
          ) : (
            header
          )}
        </div>
      )}
      <div>{children}</div>
      {footer && (
        <div className="mt-4 border-t border-eg-bordure pt-3">
          {typeof footer === 'string' ? (
            <p className="text-[13px] text-eg-gris">{footer}</p>
          ) : (
            footer
          )}
        </div>
      )}
    </div>
  );
});

Card.displayName = 'Card';

export default Card;
